import React, { useEffect, useRef, useState } from 'react'
import { FaTrash, FaPlus } from 'react-icons/fa'
import { Alert, Box, Button, DateChooser, Input, Stack, Text } from '@/components/lib'
import { tableColumns } from '@/utils/utils'

// A cell is empty when it holds nothing at all. 0 is an answer like any other,
// so only null/undefined and a blank string count — the same rule the API
// applies when it decides which rows to drop.
const cellIsEmpty = cell => cell === null || cell === undefined || (typeof cell === 'string' && !cell.trim())

const rowIsEmpty = row => Object.values(row ?? {}).every(cellIsEmpty)

function Cell ({ column, value, onChange, isDisabled }) {
  if (column.type === 'date') {
    return (
      <DateChooser
        size='sm'
        value={value || null}
        onChange={date => onChange(date ? date.toISOString() : null)}
      />
    )
  }
  const onInput = e => {
    const raw = e.target.value
    if (raw === '') return onChange(null)
    // Kept as typed rather than parsed: a half-typed "-" or "2." is not a
    // number yet, and parsing it away would make those keystrokes unenterable.
    // The API coerces each cell to its column's format on submit.
    if (column.type === 'integer' && !/^-?[0-9]*$/.test(raw)) return
    if (column.type === 'float' && !/^-?[0-9]*\.?[0-9]*$/.test(raw)) return
    onChange(raw)
  }
  return (
    <Input
      size='sm'
      bg='bg.surface'
      disabled={isDisabled}
      value={value ?? ''}
      onChange={onInput}
      placeholder={column.type === 'text' ? 'Enter text...' : 'Enter a number...'}
    />
  )
}

export default function Table ({ dataType, value, onChange, isDisabled }) {
  const columns = tableColumns(dataType)
  const maximumRows = dataType?.maximumRows || 1
  const blankRow = () => Object.fromEntries(columns.map(c => [c.id, null]))
  const storedRows = Array.isArray(value) ? value : []

  // The rows are held here as well as in the response, because a value of
  // nothing but blank rows is reported upwards as no value at all — so without
  // a local copy, the blank row the respondent just added would be taken away
  // again by the answer coming back empty.
  const [rows, setRows] = useState(() => storedRows.length ? storedRows : [blankRow()])
  const lastEmitted = useRef(value)

  useEffect(() => {
    if (value === lastEmitted.current) return
    // Both guards below are about our own "no value" coming back at us, not a
    // different response being loaded into the field.
    //
    // The parent deletes its entry when a field reports no value, and `value`
    // then falls back to whatever was already stored — for a response being
    // corrected, the very rows that were just cleared. Resyncing on that puts
    // them straight back on screen, which made a table answer impossible to
    // clear once it had been submitted.
    if (lastEmitted.current === null && rows.every(rowIsEmpty)) return
    // The same echo on a response with nothing stored, where the fallback is
    // undefined: resyncing would take away a blank row just added.
    if (!storedRows.length && rows.every(rowIsEmpty)) return
    setRows(storedRows.length ? storedRows : [blankRow()])
  }, [value])

  if (!columns.length) {
    return (
      <Alert status='warning'>
        This field has no columns set up yet, so there is nothing to fill in.
      </Alert>
    )
  }

  const emit = next => {
    setRows(next)
    const emitted = next.every(rowIsEmpty) ? null : next
    lastEmitted.current = emitted
    onChange(emitted)
  }

  const setCell = (rowIndex, columnId, cell) =>
    emit(rows.map((row, i) => i === rowIndex ? { ...row, [columnId]: cell } : row))

  const addRow = () => emit([...rows, blankRow()])

  // Removing a row leaves at least one behind, so there is always somewhere to
  // start typing again. The bin is hidden on the last row, so the fallback is
  // only a guard — it is what makes "there is always a row on screen" true
  // regardless of how this is called.
  const removeRow = rowIndex => {
    const next = rows.filter((row, i) => i !== rowIndex)
    emit(next.length ? next : [blankRow()])
  }

  return (
    <Box>
      <Box overflowX='auto'>
        <Stack gap={1} minW={{ md: `${columns.length * 10 + 4}rem` }}>
          <Stack
            direction='row' gap={2} px={2}
            display={{ base: 'none', md: 'flex' }}
          >
            {columns.map(column => (
              <Text key={column.id} flex='1' fontSize='xs' fontWeight='bold' color='fg.muted'>
                {column.name}{column.isOptional && <Text as='span' fontWeight='normal'> (optional)</Text>}
              </Text>
            ))}
            <Box w='2.5rem' />
          </Stack>

          {rows.map((row, rowIndex) => (
            <Stack
              key={rowIndex}
              direction={{ base: 'column', md: 'row' }}
              gap={2}
              alignItems={{ md: 'center' }}
              px={2} py={2}
              bg={rowIndex % 2 ? 'bg.subtle' : undefined}
            >
              {columns.map(column => (
                <Box key={column.id} flex='1'>
                  {/* The header carries the names once the row is laid out in columns */}
                  <Text display={{ base: 'block', md: 'none' }} fontSize='xs' color='fg.muted' mb={1}>
                    {column.name}{column.isOptional ? ' (optional)' : ''}
                  </Text>
                  <Cell
                    column={column}
                    value={row?.[column.id]}
                    isDisabled={isDisabled}
                    onChange={cell => setCell(rowIndex, column.id, cell)}
                  />
                </Box>
              ))}
              {/* The Box stays whatever happens, so the inputs keep lining up
                  with the header. The bin itself is only offered once there is
                  more than one row: there is always a row on screen to type
                  into, so removing the last one can only put a blank one back
                  in its place — a control that visibly does nothing when that
                  row was already blank. Clearing the single row is what
                  emptying its cells does. */}
              <Box w={{ md: '2.5rem' }}>
                {rows.length > 1 &&
                  <Button
                    variant='ghost' size='sm' aria-label='Remove row'
                    disabled={isDisabled}
                    onClick={() => removeRow(rowIndex)}
                  ><FaTrash />
                  </Button>}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Stack direction='row' justifyContent='space-between' alignItems='center' pt={2} px={2}>
        <Button
          variant='outline' size='sm'
          disabled={isDisabled || rows.length >= maximumRows}
          onClick={addRow}
        ><FaPlus /> Add a row
        </Button>
        <Text fontSize='xs' color='fg.muted'>
          {rows.filter(row => !rowIsEmpty(row)).length} of {maximumRows} row{maximumRows === 1 ? '' : 's'} filled in
        </Text>
      </Stack>
    </Box>
  )
}
